import {
	Children,
	type FunctionComponent,
	type PropsWithChildren,
	type ReactElement,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import { windowIsSmall } from '../../../helpers/breakpoints/breakpoint.helpers';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { useNotSmallBreakpointListener } from '../../../hooks/breakpoints/breakpoint-listeners.hook';
import ChevronUpIcon from '../../svgs/chevron-up.svg';
import { activeTabOffset, indicatorIcon } from './responsive-tabs.css';

type TabsVariant = 'tabs' | 'accordion';

type IndicatorProps = {
	isExpanded: boolean;
	variant: TabsVariant;
	useAnimations: boolean;
};

const Indicator: FunctionComponent<IndicatorProps> = ({ isExpanded, variant, useAnimations }) => {
	return (
		<div
			data-testid="indicator"
			className={`${variant === 'accordion' ? '' : 'dn-ns'} f-inherit lh-solid ml3 flex items-center`}
			data-automation="expand">
			<ChevronUpIcon className={`${useAnimations ? indicatorIcon : ''} ${isExpanded ? '' : 'rotate-180'} `} />
		</div>
	);
};

export type ResponsiveTabProps = {
	name: string;
	onClick?: Function;
	isActive?: boolean;
	isExpanded?: boolean;
	tabClasses?: string;
	activeTabClasses?: string;
	childContainerClasses?: string;
	inactiveTabClasses?: string;
	analyticsHook?: string;
	tabContentClasses?: string;
	variant?: TabsVariant;
	useAnimations?: boolean;
};

export const ResponsiveTab: FunctionComponent<PropsWithChildren<ResponsiveTabProps>> = ({
	name,
	children,
	onClick,
	isActive = false,
	isExpanded = false,
	tabClasses = '',
	activeTabClasses = '',
	childContainerClasses = '',
	inactiveTabClasses = '',
	analyticsHook,
	tabContentClasses = '',
	variant = 'tabs',
	useAnimations = false
}) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [maxHeight, setMaxHeight] = useState<number>(0);
	useEffect(() => {
		if (!useAnimations) return;

		const node = contentRef.current;
		if (!node) return;

		const updateHeight = () => {
			if (isExpanded) {
				setMaxHeight(node.scrollHeight);
			}
		};

		updateHeight();

		const observer = new ResizeObserver(updateHeight);
		observer.observe(node);

		return () => observer.disconnect();
	}, [isExpanded, children]);

	// Tab panels start closed on mobile. Avoid layout shift by setting display none for mobile size until client side mount.
	const [loading, setLoading] = useState(true);
	useEffect(() => {
		setLoading(false);
	}, []);
	const panelLoadingClass = loading ? 'dn db-ns' : '';

	const handleUserClick = () => onClick?.(name);
	const activeTabClassesWithOffset = `${activeTabClasses} ${activeTabOffset}`.trim();
	const className = `pointer ${tabClasses} ${isActive ? activeTabClassesWithOffset : inactiveTabClasses}`.trim();
	const animationStyle = useAnimations
		? {
				maxHeight: isExpanded ? maxHeight : 0,
				overflow: 'hidden',
				transition: 'max-height 0.2s ease-in'
		  }
		: {};

	return (
		<>
			<div
				role="tab"
				className={className}
				aria-label={`${name} tab`}
				onClick={handleUserClick}
				onKeyDown={handleKeys(['Enter', ' '], handleUserClick)}
				data-analytics={generateDataSelector('tab', analyticsHook)}
				tabIndex={0}>
				<div className={`w-100 w-auto-ns flex title justify-between ${tabContentClasses}`}>
					<span>{name}</span>
					<Indicator isExpanded={isExpanded && !loading} variant={variant} useAnimations={useAnimations} />
				</div>
			</div>
			<div
				ref={contentRef}
				style={animationStyle}
				role="tabpanel"
				className={`w-100 ${
					variant === 'tabs' ? 'order-last-ns overflow-y-auto-ns' : ''
				} ${panelLoadingClass} ${childContainerClasses}`}>
				{useAnimations ? children : isExpanded && children}
			</div>
		</>
	);
};

export type ResponsiveTabGroupChild = React.ReactElement<ResponsiveTabProps>;

export type ResponsiveTabGroupProps = {
	variant?: TabsVariant;
	allowMultipleOpen?: boolean;
	tabGroupClasses?: string;
	tabListClasses?: string;
	startingTabName: string;
	testId?: string;
	children: ResponsiveTabGroupChild | ResponsiveTabGroupChild[];
};

export const ResponsiveTabGroup: FunctionComponent<ResponsiveTabGroupProps> = ({
	variant = 'tabs',
	allowMultipleOpen = true,
	tabGroupClasses,
	children,
	startingTabName,
	tabListClasses = 'bb-ns b--theme-grey-light',
	testId
}) => {
	const [activeTab, setActiveTab] = useState(startingTabName || '');
	const [expandedTabs, setExpandedTabs] = useState(startingTabName ? [startingTabName] : []);

	// Mobile tabs start closed. Coordinates with initial styling in ResponsiveTab to avoid layout shift.
	useEffect(() => {
		if (windowIsSmall()) {
			setExpandedTabs([]);
		}
	}, []);

	// Listen for window size change to close all but the active tab.
	const notSmallCallback = useCallback(() => setExpandedTabs(activeTab ? [activeTab] : []), [activeTab]);
	useNotSmallBreakpointListener(notSmallCallback);

	const handleTabClicked = (tabName: string) => {
		if (windowIsSmall() && allowMultipleOpen) {
			// Toggle the named tab tab open/shut, leaving the rest as-is. Set it as active if opening.
			if (expandedTabs.includes(tabName)) {
				setExpandedTabs((expandedTabsState) => expandedTabsState.filter((name) => name !== tabName));
			} else {
				setActiveTab(tabName);
				setExpandedTabs((expandedTabsState) => [...expandedTabsState, tabName]);
			}
		} else {
			// Only one tab open at a time.
			setActiveTab(tabName);
			setExpandedTabs([tabName]);
		}
	};

	const tabs = Children.map(children, (child: ReactElement, tabIndex) => {
		// Child can be falsy when tab conditionally rendered
		if (!child) {
			return null;
		}

		const tabName: string | undefined = child.props.name?.trim();
		if (!tabName) {
			return null;
		}

		const clickHandler = () => {
			child.props.onClick?.();
			handleTabClicked(tabName);
		};

		return (
			<ResponsiveTab
				{...child.props}
				name={tabName}
				key={`${tabName}-${tabIndex}`}
				onClick={clickHandler}
				isActive={tabName === activeTab}
				isExpanded={expandedTabs.includes(tabName)}
				variant={variant}>
				{child.props.children}
			</ResponsiveTab>
		);
	});

	if (!tabs.length) {
		return null;
	}

	return (
		<section className={tabGroupClasses} data-testid={testId}>
			<div className={`flex flex-wrap flex-column ${variant === 'tabs' ? 'flex-row-ns' : ''} ${tabListClasses}`}>{tabs}</div>
		</section>
	);
};
